import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API, api } from "@/lib/api";
import { useData } from "@/context/DataContext";

const SID_KEY = "skk_chat_session";
const getSid = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) { s = `chat_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(SID_KEY, s); }
  return s;
};

const LINK_RE = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;
const RichText = ({ text, onNavigate }) => {
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(LINK_RE)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<Link key={m.index} to={m[2]} onClick={onNavigate} className="underline decoration-amber-400 text-amber-300 font-bold">{m[1]}</Link>);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return <span className="whitespace-pre-wrap">{parts}</span>;
};

const Bubble = ({ m, onNavigate }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div data-testid={`chat-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-amber-400 text-[#0A2818] rounded-br-sm" : "bg-[#181D1A] border border-emerald-900/60 text-[#FDFBF7]/90 rounded-bl-sm"}`}>
      <RichText text={m.text} onNavigate={onNavigate} />
    </div>
  </div>
);

async function streamReply(sid, message, onDelta) {
  const res = await fetch(`${API}/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Chat unavailable");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const data = ev.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const j = JSON.parse(data);
      if (j.error) throw new Error(j.error);
      onDelta(j.t);
    }
  }
}

export default function ChatWidget() {
  const { settings } = useData();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open || msgs.length) return;
    api.get(`/chat/${getSid()}`).then((r) => setMsgs(r.data)).catch((e) => console.warn("chat history unavailable", e));
  }, [open, msgs.length]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  if (pathname.startsWith("/admin") || settings?.ai_enabled !== true) return null;

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((p) => [...p, { role: "user", text }, { role: "assistant", text: "" }]);
    const patchLast = (fn) => setMsgs((p) => p.map((m, i) => (i === p.length - 1 ? { ...m, text: fn(m.text) } : m)));
    try {
      await streamReply(getSid(), text, (t) => patchLast((cur) => cur + t));
    } catch (err) {
      patchLast(() => err.message || "Sorry, something went wrong. Please call us!");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button data-testid="chat-open-button" onClick={() => setOpen(true)} aria-label="Ask Sir. King Kitchen"
          className="fixed right-4 bottom-28 md:bottom-6 z-40 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-emerald-700 hover:bg-emerald-600 text-[#FDFBF7] font-display font-black uppercase tracking-wide text-sm shadow-xl shadow-black/40 border border-amber-400/40 transition-transform active:scale-95">
          <MessageCircle className="w-5 h-5 text-amber-300" /> Ask Us
        </button>
      )}
      {open && (
        <div data-testid="chat-panel" className="fixed z-50 right-0 bottom-0 md:right-6 md:bottom-6 w-full md:w-[380px] h-[75vh] md:h-[560px] flex flex-col bg-[#101311] border border-emerald-900/70 md:rounded-3xl rounded-t-3xl shadow-2xl shadow-black/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A2818] border-b border-amber-500/30">
            <div>
              <div className="font-display font-black uppercase tracking-wide text-amber-300">Ask Sir. King</div>
              <div className="text-[11px] text-[#FDFBF7]/60">Menu, hours, delivery & catering questions</div>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Close chat" className="p-2 rounded-full hover:bg-white/10"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.length === 0 && <Bubble m={{ role: "assistant", text: "Wah gwaan! Ask me anything about the menu, hours, delivery or catering." }} />}
            {msgs.map((m, i) => (m.text || !busy ? <Bubble key={`${m.role}-${i}`} m={m} onNavigate={() => setOpen(false)} /> : <Loader2 key={`load-${i}`} className="w-4 h-4 animate-spin text-amber-400" />))}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-emerald-900/60 flex gap-2 pb-safe">
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={500} placeholder="Type your question…"
              className="flex-1 bg-[#181D1A] rounded-full px-4 py-3 border border-emerald-800/50 outline-none focus:border-amber-400 text-sm" />
            <button data-testid="chat-send-button" type="submit" disabled={busy || !input.trim()} aria-label="Send" className="w-11 h-11 rounded-full bg-amber-400 text-[#0A2818] flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
          <div className="text-[10px] text-center text-[#FDFBF7]/35 pb-2">AI helper · may make mistakes, call us to confirm</div>
        </div>
      )}
    </>
  );
}
